import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { publicCreationFilmCaption, type CreationFilmManifestV1 } from '@djgg/creations';
import { artifactCategory, shortCopy, type ShowArtifact } from './creationShowModel.js';
import { StripPreview } from './StripPreview.js';
import {
  directFilmScene,
  filmArtifacts,
  filmSeed,
  type FilmArrival,
  type FilmScene,
} from './creationFilmModel.js';
import './CreationFilm.css';

export interface CreationFilmProps {
  identity: string;
  title: string;
  activity: string;
  artifacts: readonly ShowArtifact[];
  active?: boolean;
  failed?: boolean;
  ready?: boolean;
  /** The last card's line for a game that fell back to a stock game. */
  notInYetLine?: string;
  failureMessage?: string;
  /** A sealed server run wins; otherwise a stable personal seed is derived locally. */
  runSeed?: string;
  /** Immutable edited replay for a published version. */
  sealedManifest?: CreationFilmManifestV1;
  onFinaleComplete?: () => void;
}

function personalSeed(): string {
  const key = 'creation-film:personal-seed';
  try {
    const stored = localStorage.getItem(key);
    if (stored) return stored;
    const value = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    localStorage.setItem(key, value);
    return value;
  } catch {
    return 'private-session';
  }
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query?.matches ?? false);
    query?.addEventListener?.('change', update);
    return () => query?.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

/** A one-node, passive 9:16 creation film. The surrounding show owns drawers and choices. */
export function CreationFilm(props: CreationFilmProps) {
  return <CreationFilmSession key={`${props.identity}:${props.runSeed ?? 'personal'}`} {...props} />;
}

function CreationFilmSession(p: CreationFilmProps) {
  const reduced = useReducedMotion();
  const seed = useMemo(() => p.runSeed ?? filmSeed(p.identity, personalSeed()), [p.identity, p.runSeed]);
  const openingScene = () => p.sealedManifest
    ? sealedScene(p.sealedManifest, 0, reduced)
    : directFilmScene({ seed, index: 0, title: p.title, artifacts: p.artifacts, history: [],
      reducedMotion: reduced });
  const known = useRef(new Set(p.artifacts.map(item => item.id)));
  const audioRef = useRef<HTMLAudioElement>(null);
  const readyRef = useRef(!!p.ready);
  const filmRef = useRef<HTMLElement>(null);
  const finalReported = useRef(false);
  const [visible, setVisible] = useState(!document.hidden);
  const [soundUnlocked, setSoundUnlocked] = useState(false);
  const [soundHintVisible, setSoundHintVisible] = useState(true);
  const [history, setHistory] = useState<FilmScene[]>([]);
  const [scene, setScene] = useState(openingScene);
  const [arrivals, setArrivals] = useState<FilmArrival[]>(() => {
    const screened = new Set(openingScene().artifactIds);
    return p.artifacts.filter(item => !screened.has(item.id))
      .map(item => ({ id: item.id, arrivalSeq: item.arrivalSeq ?? Number.MAX_SAFE_INTEGER, unscreenedScenes: 1 }))
      .sort((a, b) => a.arrivalSeq - b.arrivalSeq || a.id.localeCompare(b.id));
  });
  const [phase, setPhase] = useState<'film' | 'closing' | 'finale' | 'done'>('film');
  const materials = filmArtifacts(scene, p.artifacts);
  const soundtrack = p.artifacts.find(item => item.kind === 'audio' && item.url && !item.unavailable
    && item.eligibility !== 'inspiration-only' && item.origin !== 'reference');
  const artifactKey = p.artifacts.map(item => `${item.id}:${item.url ?? ''}`).join('|');

  useEffect(() => { readyRef.current = !!p.ready; }, [p.ready]);
  useEffect(() => {
    const update = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  useEffect(() => {
    const added = p.artifacts.filter(item => !known.current.has(item.id));
    if (!added.length) return;
    added.forEach(item => known.current.add(item.id));
    setArrivals(current => [
      ...current.filter(item => !added.some(next => next.id === item.id)),
      ...added.map(item => ({ id: item.id, arrivalSeq: item.arrivalSeq ?? Number.MAX_SAFE_INTEGER, unscreenedScenes: 0 })),
    ].sort((a, b) => a.arrivalSeq - b.arrivalSeq || a.id.localeCompare(b.id)));
  }, [artifactKey]);
  function reportFinale() {
    setPhase('done');
    if (finalReported.current) return;
    finalReported.current = true;
    p.onFinaleComplete?.();
  }

  function replay() {
    setHistory([]);
    setArrivals([]);
    setScene(openingScene());
    setPhase('film');
    if (audioRef.current) audioRef.current.currentTime = 0;
    filmRef.current?.focus({ preventScroll: true });
  }

  function nextScene() {
    const screened = new Set(scene.artifactIds);
    const nextArrivals = arrivals
      .filter(item => !screened.has(item.id))
      .map(item => ({ ...item, unscreenedScenes: item.unscreenedScenes + 1 }));
    const nextHistory = [...history, scene].slice(-12);
    setArrivals(nextArrivals);
    setHistory(nextHistory);
    if (p.sealedManifest) {
      const nextIndex = scene.index + 1;
      if (nextIndex >= p.sealedManifest.scenes.length) {
        reportFinale();
        return;
      }
      setScene(sealedScene(p.sealedManifest, nextIndex, reduced));
      return;
    }
    if (phase === 'closing') {
      setPhase('finale');
      setScene(directFilmScene({
        seed, index: scene.index + 1, title: p.title, artifacts: p.artifacts,
        history: nextHistory, arrivals: nextArrivals, reducedMotion: reduced, finale: true,
      }));
      return;
    }
    if (readyRef.current) {
      // Ready may land in the same Realtime batch as the first proof. Screen
      // one final durable arrival (proof first, then oldest by sequence) before
      // the finale, while the independent 9.5s deadline remains the hard stop.
      const ordered = [...nextArrivals].sort((a, b) => a.arrivalSeq - b.arrivalSeq || a.id.localeCompare(b.id));
      const finalArrival = ordered.find(arrival => {
        const artifact = p.artifacts.find(item => item.id === arrival.id);
        return artifact && artifactCategory(artifact) === 'proof';
      }) ?? ordered[0];
      if (finalArrival) {
        setPhase('closing');
        setScene(directFilmScene({
          seed, index: scene.index + 1, title: p.title, artifacts: p.artifacts,
          history: nextHistory,
          arrivals: [{ ...finalArrival, unscreenedScenes: Math.max(1, finalArrival.unscreenedScenes) }],
          reducedMotion: reduced,
        }));
        return;
      }
      setPhase('finale');
      setScene(directFilmScene({
        seed, index: scene.index + 1, title: p.title, artifacts: p.artifacts,
        history: nextHistory, arrivals: nextArrivals, reducedMotion: reduced, finale: true,
      }));
      return;
    }
    setScene(directFilmScene({
      seed, index: scene.index + 1, title: p.title, artifacts: p.artifacts,
      history: nextHistory, arrivals: nextArrivals, reducedMotion: reduced,
    }));
  }

  const advanceRef = useRef<() => void>(() => {});
  useEffect(() => { advanceRef.current = () => phase === 'finale' ? reportFinale() : nextScene(); });
  useEffect(() => {
    if (p.active === false || p.failed || !visible || phase === 'done') return;
    // Realtime arrivals update the next cut without restarting this phrase or
    // letting the timer keep the inventory from when the phrase began.
    const timer = window.setTimeout(() => advanceRef.current(), scene.durationMs);
    return () => clearTimeout(timer);
  }, [scene.id, phase, p.active, p.failed, visible]);
  useEffect(() => {
    if (!p.ready || p.sealedManifest || phase === 'done') return;
    // A final safety boundary: current phrase + finale must never strand Ready.
    const deadline = window.setTimeout(reportFinale, 9_500);
    return () => clearTimeout(deadline);
  }, [p.ready, p.sealedManifest, phase]);

  // Preload only the next composition's first two visuals. No hidden scene DOM
  // is mounted, and a realtime burst cannot grow an unbounded preload queue.
  useEffect(() => {
    if (p.failed || p.sealedManifest || phase !== 'film') return;
    const nextHistory = [...history, scene].slice(-12);
    const preview = directFilmScene({
      seed, index: scene.index + 1, title: p.title, artifacts: p.artifacts,
      history: nextHistory, arrivals, reducedMotion: reduced,
    });
    const images = filmArtifacts(preview, p.artifacts)
      .filter(item => item.url && item.kind !== 'audio' && item.kind !== 'video')
      .slice(0, 2)
      .map(item => {
        const image = new Image();
        image.referrerPolicy = 'no-referrer';
        image.src = item.url!;
        return image;
      });
    return () => images.forEach(image => { image.src = ''; });
  }, [scene.id, artifactKey, phase, p.failed, p.sealedManifest, reduced]);

  useEffect(() => {
    const player = audioRef.current;
    if (!player) return;
    player.volume = 0.55;
    if (soundUnlocked && !p.failed && visible && p.active !== false && phase !== 'done') void player.play().catch(() => {});
    else if (!player.paused) player.pause();
    return () => { if (!player.paused) player.pause(); };
  }, [soundUnlocked, soundtrack?.id, p.failed, visible, p.active, phase]);
  useEffect(() => {
    if (!soundtrack || soundUnlocked) return;
    setSoundHintVisible(true);
    const timer = window.setTimeout(() => setSoundHintVisible(false), 7_000);
    return () => clearTimeout(timer);
  }, [soundtrack?.id, soundUnlocked]);

  function unlockSound() {
    if (!soundtrack || soundUnlocked) return;
    setSoundUnlocked(true);
    setSoundHintVisible(false);
  }
  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.key === ' ') event.preventDefault();
    unlockSound();
  }

  return <section ref={filmRef} className={`creation-film${reduced ? ' creation-film--reduced' : ''}`}
    aria-label={`${p.title} creation film`} tabIndex={0} onClick={unlockSound} onKeyDown={onKeyDown}
    data-testid="creation-film" data-recipe={scene.recipe} data-scene-index={scene.index}
    data-scene={scene.id.split(':').at(-1)}>
    {p.failed ? <div className="creation-film__intermission" role="status">
      <span>Intermission</span><h2>Your saved work is safe</h2>
      <p>{p.failureMessage || 'Deej is keeping the finished pieces safe while this step is repaired.'}</p>
    </div> : phase === 'done' ? <div className="creation-film__final-hold">
      <span>Ready</span><h2>{p.title}</h2><p>This game is ready to play.</p>
      {p.notInYetLine && <p data-testid="creation-film-not-in-yet">{p.notInYetLine}</p>}
      <button type="button" className="creation-film__replay" data-testid="creation-film-replay"
        onClick={event => { event.stopPropagation(); replay(); }}>Replay</button>
    </div> : <FilmSceneView scene={scene} title={p.title} activity={p.activity} artifacts={materials}
      gameIsReady={!!p.ready || !!p.sealedManifest} />}
    {soundtrack?.url && <audio ref={audioRef} src={soundtrack.url} preload="none" loop aria-label={`${p.title} soundtrack`} />}
    {soundtrack && soundHintVisible && !soundUnlocked && !p.failed && <span className="creation-film__sound-hint">Tap or press Enter for sound</span>}
    <span className="creation-film__scene-label" aria-live="polite">{sceneLabel(scene, materials, p.title)}</span>
  </section>;
}

function sceneLabel(scene: FilmScene, materials: readonly ShowArtifact[], title: string): string {
  if (scene.recipe === 'title-tournament') {
    const selected = materials.find(item => item.selected && item.durableKind === 'title-candidate');
    return `Title selected: ${selected?.title ?? title}`;
  }
  if (scene.recipe === 'deej') return `Deej follows the making of ${title}`;
  if (scene.recipe === 'inspiration') return `Inspiration reference: ${materials[0]?.title ?? title}`;
  if (scene.recipe === 'soundtrack') return `${title} soundtrack`;
  return materials[0]?.title ?? title;
}

function FilmSceneView({ scene, title, activity, artifacts, gameIsReady }: {
  scene: FilmScene; title: string; activity: string; artifacts: readonly ShowArtifact[];
  /** A sealed film only exists for a published version; a live one waits for the build. */
  gameIsReady: boolean;
}) {
  const style = { '--film-layout': scene.layout } as CSSProperties;
  const written = publicCreationFilmCaption(scene.caption ?? '');
  // The soundtrack is heard through the film's one audio element, never drawn.
  const visuals = artifacts.filter(item => item.kind !== 'audio');
  // These recipes fill the frame with their first material; anything else they carry is a small inset.
  const hasMainFrame = scene.recipe === 'gameplay' || scene.recipe === 'hero';
  // The heading already says the title, so only a real picture may sit behind it.
  const backdrop = visuals.find(item => item.url);
  if (scene.recipe === 'kinetic-title') return <div className="creation-film__scene creation-film__scene--title" style={style}>
    {backdrop && <FilmMedia artifact={backdrop} />}
    <div><span>{scene.index === 0 ? 'Here we go' : gameIsReady ? 'Ready to play' : lightSceneCopy(scene).eyebrow}</span><h2>{scene.index === 0 ? scene.title ?? title : sceneHeadline(scene)}</h2>
      {scene.index === 0 && written && <p>{written}</p>}</div>
  </div>;
  if (scene.recipe === 'title-tournament') {
    const candidates = artifacts.filter(item => item.durableKind === 'title-candidate').slice(0, 6);
    const selected = candidates.find(item => item.selected) ?? candidates.find(item => item.title === title);
    return <div className="creation-film__scene creation-film__scene--title-tournament" style={style}>
      <div className="creation-film__title-tournament-heading"><span>Title tournament</span><h2>And the title is…</h2></div>
      <ol aria-label="Title candidates">{candidates.map((candidate, index) => <li key={candidate.id}
        className={candidate.id === selected?.id ? 'creation-film__title-candidate--selected' : ''}
        style={{ '--film-candidate': index } as CSSProperties}>
        <span>{String(index + 1).padStart(2, '0')}</span><strong>{candidate.title}</strong>
        {candidate.id === selected?.id && <em>Selected</em>}
      </li>)}</ol>
      <div className="creation-film__title-lock" role="status"><span>Locked in</span><strong>{selected?.title ?? title}</strong></div>
    </div>;
  }
  if (scene.recipe === 'deej') return <div className="creation-film__scene creation-film__scene--deej" style={style}>
    <DeejCard scene={scene} />
    {written && <div className="creation-film__caption"><h2>{written}</h2></div>}
  </div>;
  if (scene.recipe === 'inspiration') return <div className="creation-film__scene creation-film__scene--inspiration" style={style}>
    <div className="creation-film__inspiration-frame">{visuals[0] && <FilmMedia artifact={visuals[0]} />}</div>
    <div className="creation-film__caption"><span>Inspiration · external reference</span><h2>{visuals[0]?.title ?? 'A reference arrives'}</h2></div>
  </div>;
  if (scene.recipe === 'mechanic') return <div className="creation-film__scene creation-film__scene--mechanic" style={style}>
    <span>{lightSceneCopy(scene).eyebrow}</span><h2>{sceneHeadline(scene)}</h2>
    {/* Text is all a live run has before any art: the creator's own idea and title. */}
    {!gameIsReady && visuals[0]?.summary && !visuals[0].unavailable && <p>{shortCopy(visuals[0].summary, 140)}</p>}
  </div>;
  if (scene.recipe === 'soundtrack') return <div className="creation-film__scene creation-film__scene--soundtrack" style={style}>
    {visuals[0] && <FilmMedia artifact={visuals[0]} />}
    <div className="creation-film__record" aria-hidden="true"><i /><i /><i /><i /><i /></div>
    <div><span>{lightSceneCopy(scene).eyebrow}</span><h2>{sceneHeadline(scene)}</h2></div>
  </div>;
  return <div className={`creation-film__scene creation-film__scene--${scene.recipe}`} style={style}>
    <div className="creation-film__frames">{visuals.map((artifact, index) => <figure key={artifact.id} style={{
        '--film-item': index,
        ...(artifact.kind === 'inspiration' ? {
          '--film-intrinsic-width': `${artifact.intrinsicWidth}px`,
          '--film-intrinsic-height': `${artifact.intrinsicHeight}px`,
        } : {}),
      } as CSSProperties}
      className={hasMainFrame ? (index === 0 ? 'creation-film__main-frame' : 'creation-film__inset-frame') : undefined}>
      <FilmMedia artifact={artifact} />
      {artifact.kind === 'inspiration' && <figcaption>Inspiration · external reference</figcaption>}
    </figure>)}</div>
    <div className="creation-film__caption"><span>{lightSceneCopy(scene).eyebrow}</span>
      <h2>{sceneHeadline(scene)}</h2></div>
  </div>;
}

const LIGHT_COPY: Record<FilmScene['recipe'], ReadonlyArray<{ eyebrow: string; title: string }>> = {
  'kinetic-title': [
    { eyebrow: 'Here we go', title: 'A new adventure' }, { eyebrow: 'One bright idea', title: 'Let’s chase it' },
  ],
  'title-tournament': [{ eyebrow: 'Title tournament', title: 'And the title is…' }],
  'contact-sheet': [
    { eyebrow: 'So many possibilities', title: 'The world opens up' }, { eyebrow: 'Fresh arrivals', title: 'Every one a keeper' },
    { eyebrow: 'A splash of chaos', title: 'Something clicks' }, { eyebrow: 'Look what appeared', title: 'More magic, please' },
  ],
  hero: [
    { eyebrow: 'Look what arrived', title: 'Right at home' }, { eyebrow: 'Front and center', title: 'Born for this' },
    { eyebrow: 'Big entrance', title: 'Hello, trouble' }, { eyebrow: 'There you are', title: 'Ready for anything' },
  ],
  parallax: [
    { eyebrow: 'Coming together', title: 'A world with depth' }, { eyebrow: 'Piece by piece', title: 'Now it feels real' },
    { eyebrow: 'A little closer', title: 'The world wakes up' }, { eyebrow: 'Everything connects', title: 'That belongs there' },
  ],
  lineup: [
    { eyebrow: 'All lined up', title: 'Look at these' }, { eyebrow: 'Side by side', title: 'Made for this game' },
    { eyebrow: 'Fresh arrivals', title: 'All in one place' }, { eyebrow: 'Look closer', title: 'This could get wild' },
  ],
  mechanic: [
    { eyebrow: 'Finding the fun', title: 'The rhythm takes shape' }, { eyebrow: 'Try this', title: 'A clever little twist' },
    { eyebrow: 'One more rule', title: 'Now things get interesting' }, { eyebrow: 'Here’s the trick', title: 'Easy to learn. Maybe.' },
  ],
  inspiration: [
    { eyebrow: 'Inspiration', title: 'A reference, clearly labelled' },
    { eyebrow: 'Looking outward', title: 'Ideas, not game art' },
  ],
  cutout: [
    { eyebrow: 'A little magic', title: 'Ready to move' }, { eyebrow: 'Snip, snap', title: 'Out you come' },
    { eyebrow: 'Looking sharp', title: 'Ready to go' }, { eyebrow: 'Perfect fit', title: 'Made for motion' },
  ],
  deej: [{ eyebrow: '', title: '' }],
  soundtrack: [
    { eyebrow: 'Turn it up', title: 'A beat finds the world' }, { eyebrow: 'Listen to that', title: 'Now it has a pulse' },
    { eyebrow: 'Volume up', title: 'The world starts dancing' }, { eyebrow: 'Found the groove', title: 'Everything moves together' },
  ],
  gameplay: [
    { eyebrow: 'It’s alive', title: 'Into the action' }, { eyebrow: 'Your turn', title: 'Go for it' },
    { eyebrow: 'Here we go', title: 'See how it plays' }, { eyebrow: 'Game on', title: 'See how far it goes' },
    { eyebrow: 'Nice move', title: 'Again!' }, { eyebrow: 'Almost there', title: 'One more run' },
    { eyebrow: 'All in', title: 'Now we’re playing' }, { eyebrow: 'That’s the feeling', title: 'Don’t stop now' },
  ],
};

function lightSceneCopy(scene: FilmScene): { eyebrow: string; title: string } {
  const options = LIGHT_COPY[scene.recipe];
  return options[(scene.index + scene.layout) % options.length]!;
}

function sceneHeadline(scene: FilmScene): string {
  return publicCreationFilmCaption(scene.caption ?? '') ?? lightSceneCopy(scene).title;
}

function DeejCard({ scene }: { scene: FilmScene }) {
  if (!scene.deejAsset) return null;
  return <img className="creation-film__deej-card" src={scene.deejAsset} alt="Deej helps this part of the game come together" />;
}

function sealedScene(manifest: CreationFilmManifestV1, index: number, reducedMotion: boolean): FilmScene {
  const source = manifest.scenes[index]!;
  const recipeByScene: Partial<Record<string, FilmScene['recipe']>> = {
    spark: 'kinetic-title', rules: 'mechanic', 'raw-art': 'contact-sheet', cutout: 'cutout',
    casting: 'lineup', 'sound-design': 'soundtrack', assembly: 'parallax',
    'first-playable': 'gameplay', playtest: 'gameplay', repair: 'parallax', effects: 'contact-sheet',
    'tuned-playable': 'gameplay', 'quality-gates': 'hero', 'trailer-cut': 'gameplay',
    'explore-handoff': 'gameplay', finale: 'kinetic-title',
  };
  const mappedRecipe: FilmScene['recipe'] = recipeByScene[source.sceneId]
    ?? (source.kind === 'intro' || source.kind === 'outro' ? 'kinetic-title'
      : source.kind === 'character' ? 'lineup'
        : source.kind === 'gameplay' || source.kind === 'proof' ? 'gameplay'
          : 'parallax');
  const deejId = source.materialIds.find(id => id.startsWith('deej-film-'));
  // Older sealed edits sometimes placed a Deej card over a material scene.
  // Treat a dedicated character beat as a Deej interlude, but never overlay a
  // mascot on another recipe.
  const recipe: FilmScene['recipe'] = deejId && source.materialIds.every(id => id.startsWith('deej-film-')) ? 'deej' : mappedRecipe;
  return {
    id: `${manifest.runId}:sealed:${source.sceneId}`,
    index,
    recipe,
    compositionKey: `sealed:${source.sceneId}`,
    artifactIds: recipe === 'deej' ? [] : source.materialIds.filter(id => !id.startsWith('deej-film-')),
    durationMs: source.durationMs,
    layout: index % 6,
    reducedMotion,
    ...(deejId && recipe === 'deej' ? { deejAsset: `/mascot/${deejId}.webp` } : {}),
    title: source.title,
    caption: source.caption,
  };
}

function FilmMedia({ artifact }: { artifact: ShowArtifact }) {
  const [broken, setBroken] = useState(false);
  if (broken) return <div className="creation-film__text-material"><span>{artifactCategory(artifact)}</span><strong>{artifact.title}</strong></div>;
  if (artifact.kind === 'video' && artifact.url) return <video src={artifact.url} poster={artifact.posterUrl}
    muted autoPlay loop playsInline preload="metadata"
    aria-label={`${artifact.title} sealed gameplay replay`} onError={() => setBroken(true)} />;
  if (artifact.strip && artifact.url) return <StripPreview url={artifact.url} strip={artifact.strip} variant="cinematic" />;
  if (artifact.url) return <img src={artifact.url} alt={artifact.title}
    aria-label={artifact.kind === 'inspiration' ? `${artifact.title} external inspiration` : undefined}
    decoding="async" referrerPolicy="no-referrer"
    onError={() => setBroken(true)}
    style={artifact.kind === 'inspiration' ? {
      maxWidth: `${artifact.intrinsicWidth}px`,
      maxHeight: `${artifact.intrinsicHeight}px`,
    } : undefined} />;
  return <div className="creation-film__text-material"><span>{artifactCategory(artifact)}</span><strong>{artifact.title}</strong></div>;
}
